/**
 * "Add Manually" on the Breakdowns tab: a breakdown entered by hand, in three
 * steps — the procedures (step 1), the plan details and coverage conditions
 * nothing has answered yet (step 2), and what coverage by category still
 * misses (step 3).
 *
 * Nothing is created until step 1 is saved: the procedures go to the server
 * in one call (breakdown-manual-create with `lines`), which makes the
 * breakdown, fills the plan from the lines and returns it. Until then the
 * typing is kept in this browser per policy, so closing the drawer loses
 * nothing. Steps 2 and 3 edit that breakdown's plan and coverage, as the tab's
 * Edit plan and Edit coverage do.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Popconfirm, Steps, message } from "antd";
import dayjs from "dayjs";

import { controller as rootController } from "../../../../../controller";
import {
  controller as treatmentsController, createManualBreakdownWithLines, getManualBreakdownCodes,
} from "../../../controller";
import { describeApiError } from "../../../lib/apiError";
import { PLAN_DETAIL_ROWS } from "../../../lib/planDetails";
import CoverageTable from "../sheet/CoverageTable";
import { PlanFieldsForm, changedFields, planDraftOf } from "../sheet/PlanEditPanel";
import { categoryCardsOf, coverageConditionGroups, planDetailFields, toAskCount } from "../sheet/sheetModel";
import ProcedureEntryTable from "./ProcedureEntryTable";
import {
  clearDraft, entryRows, firstInvalid, isOrthoCode, loadDraft, prefillFromPrevious, progress, saveDraft, savePayload,
} from "./entryModel";
import "../sheet/breakdownSheet.css";
import "./manualWizard.css";

const STEPS = [
  { key: "procedures", title: "Procedures" },
  { key: "plan", title: "Plan details" },
  { key: "coverage", title: "Coverage" },
];

const SOURCE_WORDS = {
  manual: "entered by hand",
  pdf_upload: "read from a PDF",
  ai_scraper: "fetched from the carrier's portal",
  ai_phone_call: "from a call to the carrier",
};

const ORTHO_TERMS = ["ortho_payment", "ortho_initial_payment_pct"];

function termsOf(policy) {
  const p = policy || {};
  return {
    ortho_payment: p.ortho_payment && p.ortho_payment !== "unknown" ? p.ortho_payment : "",
    ortho_initial_payment_pct: p.ortho_initial_payment_pct == null ? "" : String(Number(p.ortho_initial_payment_pct)),
  };
}

// A suggested value as words: an option's label, Covered / Not covered, or the value.
function valueWords(field, value) {
  if (value === true) return "Covered";
  if (value === false) return "Not covered";
  const row = PLAN_DETAIL_ROWS.find((r) => r.field === field);
  const option = row && (row.options || []).find((o) => o.value === value);
  return option ? option.label : String(value);
}

/**
 * The facts the office's other members of this group plan agree on, as the
 * plan form's suggestions: "2 other members of this group plan: $50 · Use".
 */
export function groupPlanHints(groupPlan, onSet) {
  const out = {};
  const g = groupPlan || {};
  const who = (n) => `${n} other member${n === 1 ? "" : "s"} of this group plan`;
  Object.keys(g).forEach((field) => {
    const fact = g[field];
    if (!fact || field === "benefit_year") return;
    out[field] = { label: `${who(fact.members)}: ${valueWords(field, fact.value)}`, value: fact.value };
  });
  if (g.benefit_year && g.benefit_year.value) {
    const year = g.benefit_year.value;
    const type = (g.benefit_period_type && g.benefit_period_type.value)
      || (dayjs(year.start).format("MM-DD") === "01-01" ? "calendar_year" : "policy_year");
    out.benefit_period_type = {
      label: `${who(g.benefit_year.members)}: ${valueWords("benefit_period_type", type)} from ${dayjs(year.start).format("MMM D")}`,
      onUse: () => onSet({ benefit_period_type: type, benefit_year_start: year.start, benefit_year_end: year.end }),
    };
  }
  return out;
}

export default function ManualBreakdownWizard({ open, patient, policy, onClose, onCreated, onFinished, onPolicySaved }) {
  const policyId = policy && policy.id;
  const [step, setStep] = useState("procedures");
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState(null);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [terms, setTerms] = useState(() => termsOf(policy));
  const [dismissed, setDismissed] = useState([]);
  const [touched, setTouched] = useState(false);
  const [restored, setRestored] = useState(null);
  const [saving, setSaving] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [breakdownId, setBreakdownId] = useState(null);
  const [cov, setCov] = useState(null);
  const [planDraft, setPlanDraft] = useState(null);
  const coverageControl = useRef(null);

  // What to ask, and where the FTC left off.
  useEffect(() => {
    if (!open || !policyId) return undefined;
    let live = true;
    getManualBreakdownCodes(policyId).then((res) => {
      if (!live) return;
      if (!res || res.status >= 250) {
        setCtxError(describeApiError(res, "Couldn't load the procedures to ask."));
        return;
      }
      const data = res.json || {};
      setCtx(data);
      const draft = loadDraft(policyId);
      if (draft) {
        setRows(draft.rows);
        setCursor(draft.cursor || null);
        setTerms(draft.terms || termsOf(policy));
        setDismissed(draft.dismissed || []);
        setRestored(draft.savedAt || "");
        setTouched(true);
      } else {
        const fresh = entryRows(data.codes);
        setRows(fresh);
        setCursor(fresh.length ? { row: 0, group: "covered" } : null);
      }
    }).catch(() => { if (live) setCtxError("Couldn't load the procedures to ask."); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policyId]);

  // Keep step 1's typing until it's saved.
  useEffect(() => {
    if (!touched || breakdownId || !policyId) return;
    saveDraft(policyId, { rows, cursor, terms, dismissed });
  }, [touched, breakdownId, policyId, rows, cursor, terms, dismissed]);

  const change = (nextRows, nextCursor) => { setRows(nextRows); setCursor(nextCursor); setTouched(true); };

  function startOver() {
    clearDraft(policyId);
    const fresh = entryRows((ctx || {}).codes);
    setRows(fresh);
    setCursor(fresh.length ? { row: 0, group: "covered" } : null);
    setTerms(termsOf(policy));
    setDismissed([]);
    setRestored(null);
    setTouched(false);
  }

  async function startFromPrevious() {
    const prev = ctx && ctx.previous;
    if (!prev) return;
    setPrefilling(true);
    try {
      const res = await treatmentsController.getBreakdownCoverageAndProcedureCoveragesByPolicy(policyId, prev.id);
      const lines = res && res.status < 250 && res.json ? res.json.procedure_coverages || [] : null;
      if (!lines) { message.error("Couldn't load the last breakdown."); return; }
      const next = prefillFromPrevious(rows, lines);
      change(next, next.length ? { row: 0, group: "covered" } : null);
      message.success("Filled from the last breakdown. Check each line and confirm it.");
    } finally {
      setPrefilling(false);
    }
  }

  async function loadCoverage(bId, freshPolicy) {
    const res = await treatmentsController.getBreakdownCoverageAndProcedureCoveragesByPolicy(policyId, bId);
    const json = (res && res.status < 250 && res.json) || {};
    const next = {
      policy: Object.assign({}, policy, freshPolicy || {}, json.policy && json.policy.id === policyId ? json.policy : {}),
      rows: json.procedure_coverages || [],
      categories: categoryCardsOf(json.category_coverage),
      sharedMaxGroups: json.category_shared_max_groups || [],
      overrides: json.category_overrides || {},
      benefitYearEnd: (json.benefit_period || {}).end || json.benefit_year_end || null,
    };
    setCov(next);
    return next;
  }

  // ---- step 1: the one save ----
  async function saveProcedures() {
    const bad = firstInvalid(rows);
    if (bad) {
      setCursor({ row: bad.index, group: "covered" });
      message.error(`${rows[bad.index].code}: ${bad.error}`);
      return;
    }
    setSaving(true);
    try {
      const res = await createManualBreakdownWithLines(patient.id, policyId, savePayload(rows));
      if (!res || res.status >= 250) {
        const json = (res && res.json) || {};
        const i = json.code ? rows.findIndex((r) => r.code === json.code) : -1;
        if (i !== -1) setCursor({ row: i, group: "covered" });
        const detail = json.errors ? Object.keys(json.errors).map((k) => [].concat(json.errors[k]).join(" ")).join(" ") : "";
        message.error(json.code ? `${json.code}: ${detail || "couldn't be saved."}` : describeApiError(res, "Couldn't save the breakdown."));
        return;
      }
      const bId = res.json.id;
      setBreakdownId(bId);
      clearDraft(policyId);
      // The ortho terms are the plan's, answered on the ortho lines.
      let fresh = null;
      const was = termsOf(policy);
      const termChanges = {};
      if (rows.some((r) => isOrthoCode(r.code) && !r.skipped)) {
        ORTHO_TERMS.forEach((f) => { if (terms[f] !== was[f]) termChanges[f] = terms[f] === "" ? null : terms[f]; });
      }
      if (Object.keys(termChanges).length) {
        const pr = await rootController.updatePatientInsurancePolicy(policyId, Object.assign({ breakdown_id: bId }, termChanges));
        if (pr && pr.status === 200) fresh = pr.json;
        else message.warning("Procedures saved, but not the ortho terms. Set them in Edit plan.");
      }
      if (onCreated) onCreated(bId);
      const next = await loadCoverage(bId, fresh);
      setPlanDraft(planDraftOf(next.policy));
      setStep("plan");
      message.success("Procedures saved");
    } catch (err) {
      message.error("Couldn't save the breakdown.");
    } finally {
      setSaving(false);
    }
  }

  // ---- step 2: plan details and coverage conditions ----
  const hints = useMemo(() => groupPlanHints(ctx && ctx.group_plan,
    (values) => setPlanDraft((prev) => Object.assign({}, prev, values))), [ctx]);
  const toAsk = useMemo(() => {
    if (!cov) return 0;
    const answered = cov.rows.filter((r) => r && !r.data_unavailable);
    return toAskCount(planDetailFields({ policy: cov.policy, rows: answered, maxResetDate: cov.benefitYearEnd }),
      coverageConditionGroups({ policy: cov.policy, rows: answered }));
  }, [cov]);

  async function savePlan() {
    const changes = changedFields(cov.policy, planDraft || {});
    if (!Object.keys(changes).length) { setStep("coverage"); return; }
    setSaving(true);
    try {
      const res = await rootController.updatePatientInsurancePolicy(policyId, Object.assign({ breakdown_id: breakdownId }, changes));
      if (!res || res.status !== 200) {
        message.error((res && res.json && res.json.error) || "Couldn't save the plan");
        return;
      }
      if (onPolicySaved) onPolicySaved(res.json || {});
      await loadCoverage(breakdownId, res.json || {});
      setStep("coverage");
    } finally {
      setSaving(false);
    }
  }

  // ---- step 3: coverage by category ----
  async function saveCoverage(changes) {
    let fresh = null;
    if (Object.keys(changes.policy || {}).length) {
      const res = await rootController.updatePatientInsurancePolicy(policyId, Object.assign({ breakdown_id: breakdownId }, changes.policy));
      if (!res || res.status !== 200) {
        message.error((res && res.json && res.json.error) || "Couldn't save the coverage");
        return false;
      }
      fresh = res.json || {};
      if (onPolicySaved) onPolicySaved(fresh);
    }
    if (Object.keys(changes.set || {}).length || (changes.revert || []).length) {
      const next = Object.assign({}, cov.overrides, changes.set);
      (changes.revert || []).forEach((c) => { delete next[c]; });
      const res = await treatmentsController.updateBreakdownCategoryOverrides(breakdownId, next);
      if (!res || res.status >= 250) { message.error("Couldn't save the coverage"); return false; }
    }
    await loadCoverage(breakdownId, fresh);
    return true;
  }

  async function finish() {
    const ctl = coverageControl.current;
    if (ctl && ctl.dirty()) {
      setSaving(true);
      const ok = await ctl.save();
      setSaving(false);
      if (!ok) return;
    }
    if (onFinished) onFinished(breakdownId);
    onClose();
  }

  function close() {
    if (breakdownId && onFinished) onFinished(breakdownId);
    onClose();
  }

  // ---- drawing ----
  const p = progress(rows);
  const left = p.total - p.complete;
  const program = ctx && ctx.program;
  const prev = ctx && ctx.previous;

  function body() {
    if (ctxError) return <div className="mw-loading">{ctxError}</div>;
    if (!ctx) return <div className="mw-loading">Loading the procedures to ask…</div>;
    if (step === "procedures") {
      return (
        <>
          <p className="mw-intro">
            {`The procedures ${policy.insurance_name_display || "this carrier"} is asked for this office. Fill each line from left to right; every field is optional — leave out what the carrier didn't say.`}
          </p>
          {restored != null ? (
            <div className="mw-banner">
              <span className="grow">{`Picked up where you left off${restored ? ` (${dayjs(restored).format("MMM D, h:mm A")})` : ""}.`}</span>
              <button type="button" className="bs-link" onClick={startOver}>Start over</button>
            </div>
          ) : prev && !touched ? (
            <div className="mw-banner">
              <span className="grow">
                {`This policy has a breakdown from ${dayjs(prev.date).format("MMM D, YYYY")}${SOURCE_WORDS[prev.source] ? `, ${SOURCE_WORDS[prev.source]}` : ""}. Start from its answers and check each line?`}
              </span>
              <Button size="small" onClick={startFromPrevious} loading={prefilling}>Start from it</Button>
            </div>
          ) : null}
          <ProcedureEntryTable
            rows={rows}
            cursor={cursor}
            terms={terms}
            pmsLastDone={ctx.pms_last_done}
            dismissed={dismissed}
            onChange={change}
            onTerms={(t) => { setTerms(t); setTouched(true); }}
            onDismiss={(code) => { setDismissed(dismissed.concat(code)); setTouched(true); }}
          />
        </>
      );
    }
    if (!cov) return <div className="mw-loading">Loading the plan…</div>;
    if (step === "plan") {
      return (
        <div className="bs-fields">
          <div className="mw-step-head"><h3>Plan details and coverage conditions</h3></div>
          <p className="mw-step-sub">
            {toAsk
              ? `${toAsk} still to ask. What the procedures, the plan's rules or an earlier answer settled is shown with Change.`
              : "Everything here is answered. Change any answer, or continue."}
          </p>
          <PlanFieldsForm
            policy={cov.policy}
            rows={cov.rows}
            draft={planDraft || {}}
            onChange={(field, v) => setPlanDraft((d) => Object.assign({}, d, { [field]: v }))}
            maxResetDate={cov.benefitYearEnd}
            extraHints={hints}
            collapseAnswered
          />
        </div>
      );
    }
    return (
      <>
        <div className="mw-step-head"><h3>Coverage by category</h3></div>
        <p className="mw-step-sub">
          The coverage % comes from the procedures — or their status, like Predetermination required, when the
          carrier gave no %. Fill in the maximums and what's left; empty cells are marked.
        </p>
        <CoverageTable
          policy={cov.policy}
          categories={cov.categories}
          sharedMaxGroups={cov.sharedMaxGroups}
          rows={cov.rows}
          onSaveCoverage={saveCoverage}
          startEditing
          controlRef={coverageControl}
          noDollarMaximum={program && program.no_dollar_maximum ? program.label : null}
        />
      </>
    );
  }

  function footer() {
    if (step === "procedures") {
      const hint = p.total
        ? <><b>{`${p.complete} of ${p.total} done`}</b>{p.skipped ? ` · ${p.skipped} skipped` : ""}{". Your entries are kept until you save."}</>
        : null;
      const save = (
        <Button type="primary" loading={saving} disabled={!ctx || !rows.length}
          onClick={left ? undefined : saveProcedures}>Save procedures</Button>
      );
      return (
        <>
          <span className="mw-foot-hint">{hint}</span>
          <Button onClick={close}>Close</Button>
          {left ? (
            <Popconfirm
              title={`${left} procedure${left === 1 ? " isn't" : "s aren't"} done`}
              description="They're saved with what's typed so far; empty ones stay to ask."
              okText="Save anyway" cancelText="Keep going" onConfirm={saveProcedures} zIndex={1100}>
              {save}
            </Popconfirm>
          ) : save}
        </>
      );
    }
    if (step === "plan") {
      return (
        <>
          <span className="mw-foot-hint">The procedures are saved. What you answer here is kept when the plan is fetched again.</span>
          <Button onClick={() => setStep("coverage")} disabled={saving || !cov}>Skip</Button>
          <Button type="primary" onClick={savePlan} loading={saving} disabled={!cov}>Save and continue</Button>
        </>
      );
    }
    return (
      <>
        <span className="mw-foot-hint">Finish saves the coverage and shows the breakdown.</span>
        <Button onClick={() => setStep("plan")} disabled={saving}>Back</Button>
        <Button type="primary" onClick={finish} loading={saving} disabled={!cov}>Finish</Button>
      </>
    );
  }

  const current = STEPS.findIndex((s) => s.key === step);
  return (
    <Drawer
      className="mw-drawer"
      open={open}
      onClose={close}
      width="min(1280px, 96vw)"
      zIndex={1001}
      title={`Add a breakdown by hand${patient ? ` — ${[patient.first_name, patient.last_name].filter(Boolean).join(" ")}` : ""}`}
      footer={null}
    >
      <div className="mw-steps">
        <Steps size="small" current={current} items={STEPS.map((s) => ({ title: s.title }))} />
      </div>
      <div className="mw-body">{body()}</div>
      <div className="mw-foot">{footer()}</div>
    </Drawer>
  );
}
